import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAdminUser,
  useChangeUserRole,
  useDeleteUser,
  useSuspendUser,
  useUnsuspendUser,
} from '../useAdmin';
import type { AdminUserDetail, AdminUserRole } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminUserDetail.css';

const ROLES: AdminUserRole[] = ['standard', 'teacher', 'moderator', 'admin'];

/**
 * Admin user detail (F4). Drill-down: full per-user state +
 * role change + suspend/unsuspend + delete. The destructive
 * actions need a typed-confirmation gate so an accidental click
 * cannot wipe an account.
 */
export function AdminUserDetailPage(): JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useAdminUser(userId);

  if (isLoading) {
    return <p className="bl-admin-user__notice">Loading user…</p>;
  }
  if (isError) {
    const status = error instanceof AuthApiError ? error.status : null;
    if (status === 404) {
      return (
        <div className="bl-admin-user__notice bl-admin-user__notice--error" role="alert">
          User not found. <Link to="/admin/users">Back to users</Link>
        </div>
      );
    }
    return (
      <p className="bl-admin-user__notice bl-admin-user__notice--error" role="alert">
        Could not load user ({error instanceof Error ? error.message : 'unknown error'}).
      </p>
    );
  }
  if (!data) return <p>No data.</p>;

  return <UserDetailView user={data} onDeleted={() => navigate('/admin/users')} />;
}

function UserDetailView({
  user,
  onDeleted,
}: {
  user: AdminUserDetail;
  onDeleted: () => void;
}): JSX.Element {
  const changeRole = useChangeUserRole();
  const suspend = useSuspendUser();
  const unsuspend = useUnsuspendUser();
  const del = useDeleteUser();

  const [roleDraft, setRoleDraft] = useState<AdminUserRole>(user.role);
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');

  const isSuspended = !!user.suspended_at;
  const errorBanner = changeRole.error || suspend.error || unsuspend.error || del.error;

  return (
    <article className="bl-admin-user" aria-label={`User ${user.email}`}>
      <header className="bl-admin-user__header">
        <div>
          <p className="bl-admin-user__crumb">
            <Link to="/admin/users">← Users</Link>
          </p>
          <h2 className="bl-admin-user__email">{user.email || '(no email)'}</h2>
          <p className="bl-admin-user__sub">
            <span className={`bl-admin-user__role bl-admin-user__role--${user.role}`}>
              {user.role}
            </span>
            <span aria-hidden="true"> · </span>
            {isSuspended ? (
              <span className="bl-admin-user__suspended">Suspended</span>
            ) : (
              <span className="bl-admin-user__active">Active</span>
            )}
          </p>
        </div>
      </header>

      {errorBanner && (
        <p className="bl-admin-user__error" role="alert">
          {errorBanner instanceof AuthApiError
            ? errorBanner.detail || errorBanner.message
            : (errorBanner as Error).message}
        </p>
      )}

      <section className="bl-admin-user__grid">
        <Stat label="Projects" value={user.project_count} />
        <Stat label="Submissions" value={user.submission_count} />
        <Stat label="Published apps" value={user.published_app_count} />
        <Stat label="Birth year" value={user.birth_year ?? '—'} />
      </section>

      {isSuspended && (
        <section className="bl-admin-user__section bl-admin-user__suspension">
          <h3>Suspended</h3>
          <p className="bl-admin-user__sub">
            Since {formatDate(user.suspended_at)}. Reason:{' '}
            <em>{user.suspended_reason || '(no reason given)'}</em>
          </p>
        </section>
      )}

      <section className="bl-admin-user__section">
        <h3>Role</h3>
        <p className="bl-admin-user__hint">
          Change what this user can do in the app. Demoting an admin while they&apos;re the last one
          on the platform will be refused — promote someone else first.
        </p>
        <form
          className="bl-admin-user__row"
          onSubmit={(e) => {
            e.preventDefault();
            if (roleDraft === user.role) return;
            changeRole.mutate({ id: user.id, role: roleDraft });
          }}
        >
          <select
            value={roleDraft}
            onChange={(e) => setRoleDraft(e.target.value as AdminUserRole)}
            aria-label="New role"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button type="submit" disabled={roleDraft === user.role || changeRole.isPending}>
            {changeRole.isPending ? 'Saving…' : 'Save role'}
          </button>
        </form>
      </section>

      <section className="bl-admin-user__section">
        <h3>{isSuspended ? 'Unsuspend' : 'Suspend'}</h3>
        {isSuspended ? (
          <>
            <p className="bl-admin-user__hint">
              Restore this user&apos;s ability to publish, comment, and report.
            </p>
            <button
              type="button"
              onClick={() => unsuspend.mutate({ id: user.id })}
              disabled={unsuspend.isPending}
              className="bl-admin-user__btn"
            >
              {unsuspend.isPending ? 'Working…' : 'Unsuspend'}
            </button>
          </>
        ) : (
          <>
            <p className="bl-admin-user__hint">
              Suspended users can still sign in (so they can read appeals) but cannot submit,
              publish, or report.
            </p>
            <form
              className="bl-admin-user__row"
              onSubmit={(e) => {
                e.preventDefault();
                if (!reason.trim()) return;
                suspend.mutate(
                  { id: user.id, reason: reason.trim() },
                  { onSuccess: () => setReason('') },
                );
              }}
            >
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you suspending this account?"
                maxLength={500}
                aria-label="Suspension reason"
              />
              <button
                type="submit"
                disabled={!reason.trim() || suspend.isPending}
                className="bl-admin-user__btn bl-admin-user__btn--warn"
              >
                {suspend.isPending ? 'Working…' : 'Suspend'}
              </button>
            </form>
          </>
        )}
      </section>

      <section className="bl-admin-user__section bl-admin-user__danger">
        <h3>Delete account</h3>
        <p className="bl-admin-user__hint">
          Permanent. Deletes the user and cascades to their projects, samples, submissions, and
          published apps. Type the email to confirm.
        </p>
        <form
          className="bl-admin-user__row"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmDelete !== user.email) return;
            del.mutate({ id: user.id }, { onSuccess: () => onDeleted() });
          }}
        >
          <input
            type="text"
            value={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.value)}
            placeholder={user.email}
            aria-label="Type email to confirm delete"
          />
          <button
            type="submit"
            disabled={confirmDelete !== user.email || del.isPending}
            className="bl-admin-user__btn bl-admin-user__btn--danger"
          >
            {del.isPending ? 'Deleting…' : 'Delete account'}
          </button>
        </form>
      </section>

      <footer className="bl-admin-user__meta">
        <dl>
          <dt>User ID</dt>
          <dd>
            <code>{user.id}</code>
          </dd>
          <dt>Joined</dt>
          <dd>{formatDate(user.created_at)}</dd>
          <dt>Last seen</dt>
          <dd>{formatDate(user.last_seen_at)}</dd>
          <dt>Updated</dt>
          <dd>{formatDate(user.updated_at)}</dd>
        </dl>
      </footer>
    </article>
  );
}

function Stat({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <div className="bl-admin-user__stat">
      <p className="bl-admin-user__stat-label">{label}</p>
      <p className="bl-admin-user__stat-value">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
